import { Form, Input } from 'antd';
import type { FormItemProps } from 'antd';
import { cloneElement, useState, type ReactElement, type ReactNode } from 'react';

interface Props extends Omit<FormItemProps, 'label' | 'children'> {
  label: string;
  name: string;
  required?: boolean;
  children: ReactElement;
  /**
   * Optional node rendered absolute at the top-right of the field wrapper.
   * Useful for icons (e.g. sync) on TextArea where antd's `suffix` prop
   * isn't supported.
   */
  suffix?: ReactNode;
}

export function FloatingField({
  label,
  name,
  required,
  children,
  suffix,
  className,
  ...rest
}: Props) {
  const [focused, setFocused] = useState(false);
  const value = Form.useWatch(name);
  const hasValue = value !== undefined && value !== null && value !== '';
  const isFloating = focused || hasValue;

  // TextArea needs different vertical alignment — anchor label to top.
  const isTextArea = children.type === Input.TextArea;

  // Don't overwrite placeholder — children may need it (e.g. DatePicker shows
  // 'DD/MM/YYYY' below the floated label). Only inject focus handlers.
  const childProps = children.props as {
    onFocus?: (e: unknown) => void;
    onBlur?: (e: unknown) => void;
  };
  const childWithEvents = cloneElement(children, {
    onFocus: (e: unknown) => {
      setFocused(true);
      childProps.onFocus?.(e);
    },
    onBlur: (e: unknown) => {
      setFocused(false);
      childProps.onBlur?.(e);
    },
  });

  const wrapperClass = [
    'floating-field',
    isFloating ? 'is-floating' : '',
    isTextArea ? 'has-textarea' : '',
    suffix ? 'has-suffix' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass}>
      <Form.Item name={name} {...rest}>
        {childWithEvents}
      </Form.Item>
      <span className="floating-field-label">
        {label}
        {required && <span className="floating-field-required">*</span>}
      </span>
      {suffix && <span className="floating-field-suffix">{suffix}</span>}
    </div>
  );
}
